import type { Agent, CmsDocument, QuoteRecord, Ticket } from '../types';
import { buildSkyluxTicketingQuoteMailHtml } from './skyluxClientMailLayouts';
import { normalizePassengers, passengerTicketRows } from './passengers';
import { createQuote, patchQuoteDelivery, sendTransactionalEmail } from './api';
import { formatEmailLogFailureFooter, formatEmailLogSuccessFooter } from './emailLogDelivery';
import { buildPaymentUrl, buildViewTicketUrl } from './hosts';
import { ensureEticketNumber } from './skyluxReceiptIds';
import { appendEmailLog, loadCms, saveQuote } from './skyluxStore';

export type SendTicketReceiptResult = {
  ok: boolean;
  message: string;
  quote: QuoteRecord;
};

export type SendTicketReceiptOptions = {
  /** Send again even if a ticket email was already recorded. */
  resend?: boolean;
  cms?: CmsDocument;
};

function resolveClientEmail(quote: QuoteRecord, ticket: Ticket): string {
  return quote.clientSnapshot?.email?.trim() || ticket.clientEmail?.trim() || '';
}

function quoteForPersistence(quote: QuoteRecord, eTicketNumber: string): QuoteRecord {
  const rows = passengerTicketRows({ ...quote, eTicketNumber });
  const travelers = normalizePassengers(quote);
  const passengers =
    travelers.length > 0
      ? travelers.map((p, i) => ({
          ...p,
          eTicketNumber: rows[i]?.eTicketNumber ?? eTicketNumber,
        }))
      : quote.passengers;
  return {
    ...quote,
    eTicketNumber,
    ...(passengers?.length ? { passengers } : {}),
  };
}

async function persistQuoteAfterTicketSend(quote: QuoteRecord): Promise<void> {
  saveQuote(quote);
  try {
    await patchQuoteDelivery(quote.id, {
      paymentStatus: quote.paymentStatus,
      emailsSent: quote.emailsSent,
      eTicketNumber: quote.eTicketNumber,
    });
  } catch {
    try {
      await createQuote(quote);
    } catch {
      // Local storage already updated; server sync can be retried from ticket desk.
    }
  }
}

/** Standard e-ticket email (View ticket + View invoice) after payment. */
export async function sendTicketReceiptEmail(
  quote: QuoteRecord,
  ticket: Ticket,
  agent: Agent,
  options?: SendTicketReceiptOptions,
): Promise<SendTicketReceiptResult> {
  const to = resolveClientEmail(quote, ticket);
  if (!to) {
    return {
      ok: false,
      message: 'No client email on this quote or ticket. Add an email on the client profile and refresh.',
      quote,
    };
  }
  if (quote.paymentStatus !== 'paid') {
    return { ok: false, message: 'Payment has not been received yet. Send the e-ticket after the client pays.', quote };
  }
  if (quote.emailsSent?.ticket && !options?.resend) {
    return { ok: false, message: 'E-ticket email was already sent for this quote.', quote };
  }

  const cmsDoc = options?.cms ?? loadCms();
  const eTicketNumber = ensureEticketNumber(quote.eTicketNumber);
  const quoteReady = quoteForPersistence(
    { ...quote, paymentStatus: 'paid' },
    eTicketNumber,
  );
  const viewTicketUrl = buildViewTicketUrl(eTicketNumber);
  const confirmationNumber = quoteReady.confirmationNumber ?? '—';
  const subject = `SkyLux Travel Ticket Receipt. Confirmation number: ${confirmationNumber}`;
  const travelers = normalizePassengers(quoteReady);
  const ticketRows = passengerTicketRows(quoteReady);
  const passengersPlain = ticketRows.map((row) => `${row.name} — ${row.eTicketNumber}`).join('\n');
  const preview = [
    cmsDoc.quoteEmailIntro,
    '',
    `SkyLux Travel Ticket Receipt. Confirmation number: ${confirmationNumber}`,
    `Quote reference: ${quoteReady.id}`,
    `Support ticket: ${ticket.code}`,
    '',
    passengersPlain,
    '',
    `View ticket: ${viewTicketUrl}`,
    `View invoice: ${buildPaymentUrl(quoteReady.id, { view: 'invoice' })}`,
  ].join('\n');

  let html: string;
  try {
    html = buildSkyluxTicketingQuoteMailHtml({
      clientName: travelers[0]?.name ?? quoteReady.clientSnapshot.name,
      introText: cmsDoc.quoteEmailIntro,
      flight: quoteReady.flight,
      pricePerPerson: quoteReady.pricePerPerson,
      passengers: travelers,
      bookingUrl: quoteReady.bookingUrl,
      paymentUrl: quoteReady.paymentUrl,
      confirmationNumber,
      eTicketNumber,
      supportTicketCode: ticket.code,
      quoteId: quoteReady.id,
      signatureName: agent.name,
      signatureEmail: agent.email,
      primaryCtaUrl: viewTicketUrl,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Could not build e-ticket email';
    return { ok: false, message: msg, quote };
  }

  let fromAddr = agent.email;
  let footer = '';
  try {
    const mail = await sendTransactionalEmail({ to, subject, text: preview, html });
    fromAddr = mail.from;
    footer = `Sent via SMTP from ${mail.from}${mail.messageId ? ` (id: ${mail.messageId})` : ''}`;
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to,
      subject,
      kind: 'ticket_receipt',
      preview: preview + formatEmailLogFailureFooter(msg),
      deliveryStatus: 'failed',
      errorMessage: msg,
      quoteId: quoteReady.id,
    });
    return { ok: false, message: `Email not delivered: ${msg}`, quote: quoteReady };
  }

  const sentAt = new Date().toISOString();
  const updated: QuoteRecord = {
    ...quoteReady,
    emailsSent: { ...quoteReady.emailsSent, ticket: sentAt },
  };
  await persistQuoteAfterTicketSend(updated);

  appendEmailLog({
    id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    at: sentAt,
    agentId: agent.id,
    agentEmail: fromAddr,
    to,
    subject,
    kind: 'ticket_receipt',
    preview: `${preview}\n\n— Ticket email${options?.resend ? ' (resend)' : ''}: View ticket + View invoice · ${footer}`,
    deliveryStatus: 'sent',
    quoteId: updated.id,
  });

  return {
    ok: true,
    message: options?.resend ? `E-ticket resent to ${to}.` : `E-ticket email sent to ${to}.`,
    quote: updated,
  };
}
